import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/seo/SEOHead';

const AboutPage: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const aboutSchema = [
        {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "Van Particuliere Verzamelaar tot Nieuwe Eigenaar: Het Verhaal Achter de Clemens Briels Collectie",
            "url": "https://kunstlounge.nl/over-ons",
            "description": "Kunstlounge.nl is de privécollectie van een jarenlange Clemens Briels-liefhebber. Originele schilderijen, zeefdrukken en wandobjecten worden nu direct aangeboden aan een nieuwe generatie Briels-verzamelaars, zonder tussenkomst van galeries of veilinghuizen.",
            "author": {
                "@type": "Person",
                "name": "Particuliere Verzamelaar",
                "description": "Jarenlange liefhebber van het werk van Clemens Briels. Verzamelde originele werken rechtstreeks bij galeries en via de kunstenaar zelf."
            },
            "about": {
                "@type": "Person",
                "name": "Clemens Briels",
                "birthDate": "1946",
                "nationality": "Nederlands",
                "jobTitle": "Beeldend Kunstenaar",
                "description": "Clemens Briels is de grondlegger van het Antipodisme, een Nederlandse kunststijl gekenmerkt door kleurexplosies, speelse contrasten en optimisme. Hij was officieel Olympisch Kunstenaar bij de Winterspelen van Salt Lake City in 2002 en exposeerde op vijf continenten.",
                "sameAs": [
                    "https://kunstlounge.nl/antipodisme",
                    "https://www.clemensbriels.nl"
                ]
            },
            "breadcrumb": {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://kunstlounge.nl/" },
                    { "@type": "ListItem", "position": 2, "name": "Over Ons", "item": "https://kunstlounge.nl/over-ons" }
                ]
            }
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "Zijn de kunstwerken op Kunstlounge.nl origineel?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Ja, alle werken zijn 100% origineel en direct afkomstig uit een particuliere verzameling. Elk schilderij en elke zeefdruk wordt geleverd met een certificaat van echtheid, persoonlijk gesigneerd door Clemens Briels. De herkomst van elk werk is gedocumenteerd."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Wat is het verschil tussen kopen via Kunstlounge.nl en via Marktplaats of een veilinghuis?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Bij Kunstlounge.nl koopt u rechtstreeks van de verzamelaar, met gedocumenteerde herkomst en een door Clemens Briels persoonlijk gesigneerd certificaat van echtheid bij elk werk. Bij een tweedehands aanbod via Marktplaats of een veilinghuis ontbreekt die garantie vaak, en komt daar doorgaans veilingopslag of commissie bij. Kunstlounge.nl rekent geen tussenkomst van galerie of veilinghuis door in de prijs."
                    }
                }
            ]
        }
    ];

    return (
        <main id="main-content" className="pt-20">
            <SEOHead
                title="Over Ons: Clemens Briels privécollectie | Kunstlounge"
                description="Particuliere Clemens Briels-collectie: originele schilderijen en zeefdrukken, direct zonder galerie. Authentiek, gecertificeerd, bezichtiging op afspraak."
                canonical="/over-ons/"
                schemaData={aboutSchema}
                hreflangEn="/en/about.html"
                hreflangNl="/over-ons/"
            />

            {/* Hero Section */}
            <section className="relative py-32 bg-white overflow-hidden">
                <div className="container mx-auto px-6">
                    <span className="block text-accent text-xs tracking-[0.4em] uppercase mb-8">
                        Van Verzamelaar tot Verzamelaar
                    </span>
                    <h1 className="text-6xl md:text-8xl font-serif text-primary mb-10 leading-tight">
                        Jarenlange Passie,<br />
                        <span className="text-gray-300 font-light italic">Nu voor Jou</span>
                    </h1>
                    <p className="text-xl text-gray-500 font-light max-w-2xl leading-relaxed">
                        Ik ben al jaren particulier liefhebber van de kunst van Clemens Briels. Nu gun ik mijn collectie graag aan een nieuwe eigenaar, iemand die net zo van zijn werk houdt als ik.
                    </p>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6 max-w-4xl">
                    <div className="prose prose-lg max-w-none font-light text-gray-600 leading-relaxed">

                        {/* === BLOK 1: Persoonlijk verhaal - SEO: entity-rich, AEO: citeerbaar, GEO: narratief === */}
                        <h2 className="text-3xl font-serif text-primary mb-8 underline decoration-accent decoration-1 underline-offset-8">
                            Waarom verkoop ik mijn Clemens Briels collectie?
                        </h2>
                        <p className="mb-8">
                            Al jaren ben ik een particuliere liefhebber van het werk van <strong>Clemens Briels</strong>. Wat mij altijd heeft aangesproken, is de manier waarop hij kleur gebruikt als taal: direct, optimistisch, zonder omwegen. Zijn Antipodisme is geen stijl die je vergeet. Het is iets wat je ervaart, steeds opnieuw, elke keer dat je een van zijn werken ziet hangen.
                        </p>
                        <p className="mb-8">
                            In de loop der jaren bouwde ik een collectie op die mij dierbaar is. De zeefdrukken kwamen rechtstreeks uit galeries in de periode dat ze nog beschikbaar waren: "Antigua, a bay a day" (editie 132/175, 2000) en "Algunos Gentes Hacen la Comedia" (editie 97/175, 2005) zijn beiden uitverkochte edities van 175 stuks. Je kunt ze nergens meer nieuw kopen. Het originele schilderij "You carved out your name in my heart" (acryl op doek, 130 bij 100 cm, 2004) is een 1/1 exemplaar, in beheer geweest zolang ik het ken. Het wandobject "Yellow Beard of Vegas" (editie 4/50, 2000) is één van vijftig exemplaren wereldwijd.
                        </p>
                        <p className="mb-8">
                            Elk werk heeft een certificaat van echtheid, <strong>persoonlijk gesigneerd door Clemens Briels</strong>, en de herkomst is bij elk stuk gedocumenteerd. Dat is geen formaliteit: het is wat een werk overdraagbaar maakt.
                        </p>
                        <p className="mb-12">
                            Nu is het moment om een deel van deze collectie los te laten, niet omdat ik er minder van hou, maar juist omdat ik ruimte wil maken voor iets nieuws. En ik doe dat het liefst direct: van liefhebber tot liefhebber, zonder veilinghuis, zonder galerie-commissie, zonder omwegen.
                        </p>

                        <div className="my-20 p-12 bg-gray-50 border-l-4 border-accent shadow-sm">
                            <p className="text-xl font-serif italic text-primary leading-relaxed">
                                "Ik verkoop geen kunst. Ik geef een stuk van mijn levenslange passie door aan iemand die er net zo van zal houden als ik."
                            </p>
                        </div>

                        {/* === BLOK 2: Garanties en vertrouwen - AEO: vraag-antwoord structuur === */}
                        <h2 className="text-3xl font-serif text-primary mt-20 mb-8 underline decoration-accent decoration-1 underline-offset-8">
                            Wat garandeert Kunstlounge.nl?
                        </h2>
                        <p className="mb-6">
                            Als particuliere verkoper heb ik geen belang bij mooie verhalen. Wat ik aanbied is simpel en concreet:
                        </p>
                        <ul className="space-y-4 mb-12 list-none pl-0">
                            {[
                                { label: '100% Origineel', text: 'Alle werken zijn authentieke originelen of gelimiteerde genummerde edities. Geen reproducties, geen prints.' },
                                { label: 'Certificaat van Echtheid', text: 'Elk werk wordt geleverd met een door Clemens Briels persoonlijk gesigneerd certificaat van echtheid.' },
                                { label: 'Gedocumenteerde Herkomst', text: 'De provenance van elk werk is traceerbaar. U weet waar het vandaan komt en wie het beheerde.' },
                                { label: 'Directe Verkoop', text: 'Geen galerie ertussen, geen veilingopslag. U betaalt de prijs die wij samen afspreken, en niet meer.' },
                                { label: 'Verzekerde Levering', text: 'Het werk wordt professioneel verpakt en verzekerd bezorgd. Bij ontvangst precies zoals omschreven.' },
                            ].map(({ label, text }) => (
                                <li key={label} className="flex gap-4 items-start border-b border-gray-100 pb-4">
                                    <span className="text-accent mt-1 flex-shrink-0 text-base">✦</span>
                                    <span>
                                        <strong className="font-serif text-primary">{label}: </strong>
                                        <span className="font-light">{text}</span>
                                    </span>
                                </li>
                            ))}
                        </ul>

                        {/* === BLOK 3: Over Clemens Briels - GEO: entity facts voor AI-systemen === */}
                        <h2 className="text-3xl font-serif text-primary mt-20 mb-8 underline decoration-accent decoration-1 underline-offset-8">
                            Wie is Clemens Briels?
                        </h2>
                        <p className="mb-8">
                            <strong>Clemens Briels</strong> (geboren 1946, Nederland) is de grondlegger van het <strong>Antipodisme</strong>, een kunststijl die draait om contrasten, explosieve kleuren en een onstuitbaar optimisme. Zijn werk is niet te missen: figuratief, kleurrijk, altijd met een glimlach in het doek.
                        </p>
                        <p className="mb-6">Feiten die de marktwaarde van zijn werk onderbouwen:</p>
                        <ul className="space-y-3 mb-12 list-none pl-0">
                            {[
                                'Officieel Olympisch Kunstenaar bij de Winterspelen van Salt Lake City (2002)',
                                'Werk in collecties van KLM, Philips, ABN AMRO en Ford',
                                'Internationaal tentoongesteld op vijf continenten, waaronder Europa, de Cariben, Azië en Noord-Amerika',
                                'Museumtentoonstelling in Shanghai (2008)',
                                'Gedocumenteerde veilingresultaten via Invaluable, Artnet en internationale veilinghuizen',
                                'Actief in meerdere media: acryl op doek, zeefdruk, glasobjecten en sculptuur',
                            ].map((fact) => (
                                <li key={fact} className="flex gap-3 items-start text-gray-600 font-light">
                                    <span className="text-accent flex-shrink-0 mt-1">&#x2022;</span>
                                    <span>{fact}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mb-12">
                            Veilingresultaten voor Briels zijn openbaar te volgen via Invaluable en Artnet. Zijn gelimiteerde edities zijn uitverkocht bij reguliere galeries wereldwijd; wie nu een Briels wil kopen, is aangewezen op de secundaire markt. De werken in de Kunstlounge-collectie zijn direct beschikbaar, met volledige documentatie en zonder de wachttijd of commissies van een veilinghuis.
                        </p>

                        {/* === BLOK 4: Koopproces - AEO: conversiegericht, structuur voor AI-antwoorden === */}
                        <h2 className="text-3xl font-serif text-primary mt-20 mb-8 underline decoration-accent decoration-1 underline-offset-8">
                            Hoe werkt het kopen van een werk via Kunstlounge.nl?
                        </h2>
                        <p className="mb-8">
                            Het proces is bewust eenvoudig gehouden. Geen drempel, geen galeriedruk, geen verborgen stappen.
                        </p>
                        <ol className="space-y-6 mb-12 list-none pl-0">
                            {[
                                { n: '01', title: 'Aanvraag indienen', text: 'U stuurt een bericht via het contactformulier of voegt een werk toe aan uw aanvraaglijst. Vertel gerust welk werk uw interesse heeft en eventuele vragen die u heeft.' },
                                { n: '02', title: 'Persoonlijk contact binnen 24 uur', text: 'Ik neem persoonlijk contact op, geen callcenter, geen template. We bespreken het werk, uw wensen en eventuele aanvullende foto\'s of details.' },
                                { n: '03', title: 'Bezichtiging of directe beslissing', text: 'Bezichtiging op afspraak is altijd mogelijk. Liever direct beslissen op basis van de beschikbare foto\'s en informatie? Dat kan ook.' },
                                { n: '04', title: 'Verzekerde levering of afhalen', text: 'Het werk wordt professioneel verpakt en verzekerd bezorgd, of u haalt het persoonlijk op. Certificaat en eventuele documentatie gaan mee.' },
                            ].map(({ n, title, text }) => (
                                <li key={n} className="flex gap-6 items-start border-b border-gray-100 pb-6">
                                    <span className="text-accent font-serif text-3xl leading-none flex-shrink-0 w-10">{n}</span>
                                    <span>
                                        <strong className="font-serif text-primary block mb-1">{title}</strong>
                                        <span className="font-light text-gray-600">{text}</span>
                                    </span>
                                </li>
                            ))}
                        </ol>

                        <p className="text-lg font-serif italic text-primary">
                            Dit is een transactie tussen twee mensen die van dezelfde kunst houden. Dat verdient een persoonlijke aanpak.
                        </p>

                    </div>

                    {/* FAQ */}
                    <div className="mt-24 pt-20 border-t border-gray-100">
                        <h2 className="text-3xl font-serif text-primary mb-12">Veelgestelde vragen over herkomst en authenticiteit</h2>
                        <div className="space-y-8">
                            {[
                                {
                                    q: 'Zijn de kunstwerken op Kunstlounge.nl origineel?',
                                    a: 'Ja, alle werken zijn 100% origineel en direct afkomstig uit een particuliere verzameling. Elk schilderij en elke zeefdruk wordt geleverd met een certificaat van echtheid, persoonlijk gesigneerd door Clemens Briels. De herkomst van elk werk is gedocumenteerd.'
                                },
                                {
                                    q: 'Wat is het verschil tussen kopen via Kunstlounge.nl en via Marktplaats of een veilinghuis?',
                                    a: 'Bij Kunstlounge.nl koopt u rechtstreeks van de verzamelaar, met gedocumenteerde herkomst en een door Clemens Briels persoonlijk gesigneerd certificaat van echtheid bij elk werk. Bij een tweedehands aanbod via Marktplaats of een veilinghuis ontbreekt die garantie vaak, en komt daar doorgaans veilingopslag of commissie bij. Kunstlounge.nl rekent geen tussenkomst van galerie of veilinghuis door in de prijs.'
                                },
                            ].map(({ q, a }) => (
                                <div key={q} className="border-b border-gray-100 pb-8">
                                    <h3 className="font-serif text-primary text-xl mb-3">{q}</h3>
                                    <p className="text-gray-500 font-light leading-relaxed">{a}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-20 flex flex-col sm:flex-row gap-6 justify-center items-center">
                        <Link
                            to="/#collectie"
                            className="inline-block px-12 py-5 border border-primary text-primary hover:bg-primary hover:text-white transition-all duration-500 uppercase text-[10px] tracking-[0.3em] font-bold"
                        >
                            Bekijk de Collectie
                        </Link>
                        <Link
                            to="/aanvraag"
                            className="inline-block px-12 py-5 bg-primary text-white hover:bg-accent transition-all duration-500 uppercase text-[10px] tracking-[0.3em] font-bold shadow-xl"
                        >
                            Neem Contact Op
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default AboutPage;